import { useMemo, useState } from 'react';
import {
  Box,
  Container,
  Modal,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  TableHead,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import styles from '../../../assets/styles/resetPassword';
import CustomTypography from '../../../components/Typography/typography';

const SubscriptionDetails = ({ open, handlePopUp, subsDetails, user }: any) => {
  const classes = styles();
  // if (!user) return null;
  const filteredData = useMemo(() => {
    if (!Array.isArray(subsDetails)) return [];

    let hasFree = false;
    const seenStartDates = new Set<string>();

    // ✅ Sort by subsId descending
    const sortedSubs = [...subsDetails].sort((a, b) => b.subsId - a.subsId);

    return sortedSubs.filter((item) => {
      const date = new Date(item.startDate);
      // Normalize start date to minute precision (ignore seconds/milliseconds)
      const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}-${date.getMinutes()}`;

      // Skip if same minute already exists
      if (seenStartDates.has(key)) return false;
      seenStartDates.add(key);

      // Keep only the first "Free" entry
      if (item.subsType?.toLowerCase() === 'free') {
        if (hasFree) return false;
        hasFree = true;
      }

      return true;
    });
  }, [subsDetails]);

  return (
    <Container maxWidth="sm">
      <Modal
        open={open}
        onClose={handlePopUp}
        aria-labelledby="subscription-modal-title"
        aria-describedby="subscription-modal-description"
      >
        <Box className={classes.boxClass} sx={{
          position: 'relative',
          p: 3,
          border: 'none',
          outline: 'none',
          bgcolor: 'white',
          borderRadius: 2,
          maxHeight: '60vh', // ✅ limit modal height
          minWidth: '100vh', // ✅ limit modal height
          width: '100vh', // ✅ limit modal height
          overflowY: 'auto', // ✅ make scrollable
          boxShadow: 24,
          scrollbarWidth: 'none', // ✅ Firefox
          '&::-webkit-scrollbar': {
            display: 'none', // ✅ Chrome, Safari, Edge
          },
        }}>
          {/* Close Button */}
          <CloseIcon
            fontSize="medium"
            onClick={handlePopUp}
            className={classes.closeIconClass}
            color="info"
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              cursor: 'pointer',
            }}
          />

          {/* Title */}
          <CustomTypography align="center" variant="h5" component="h2" sx={{ mb: 0 }}>
            {user?.firstName} {user?.lastName}  Subscription Details
          </CustomTypography>

          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 'none',
              borderRadius: 2,
              maxHeight: 350,
              overflowY: 'auto',
              '&::-webkit-scrollbar': { display: 'none' }, // hide scroll bar
            }}
          >
            <Table>
              {/* ---------- Header ---------- */}
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 750 }}>App Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Transaction Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Expiry Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Renew Date</TableCell>
                  <TableCell sx={{ fontWeight: 750 }}>Plan Status</TableCell>
                </TableRow>
              </TableHead>

              {/* ---------- Body ---------- */}
              <TableBody>
                {filteredData?.length > 0 ? (
                  filteredData.map((item: any, index: number) => (
                    <TableRow key={item._id || index}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {item.subsType}
                          {(item.purchaseToken || item.subsEmail) && (
                            <Tooltip title={item.purchaseToken || item.subsEmail} arrow>
                              <InfoIcon
                                fontSize="inherit"
                                sx={{
                                  fontSize: '0.875rem',
                                  cursor: 'pointer',
                                  color: 'info.main',
                                  '&:hover': {
                                    color: 'info.dark',
                                  },
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{item.appType}</TableCell>
                      <TableCell>{item?.transactionDate ? new Date(item?.transactionDate).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>{new Date(item.startDate).toLocaleDateString()}</TableCell>
                      <TableCell>{new Date(item.expiryDate).toLocaleDateString()}</TableCell>
                      <TableCell>{new Date(item.renewDate).toLocaleDateString()}</TableCell>
                      <TableCell
                        sx={{
                          color:
                            item.plan.toLowerCase() === 'active'
                              ? 'green'
                              : item.plan.toLowerCase() === 'inactive'
                                ? 'red'
                                : 'gray',
                          fontWeight: 600,
                        }}
                      >
                        {item.plan}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography>No subscription details available.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

            </Table>
          </TableContainer>
        </Box>
      </Modal>
    </Container>
  );
};

export default SubscriptionDetails;
